import { Component, OnInit } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import _ from 'lodash';

import { ImageModalComponent } from 'src/app/components/image-modal/image-modal.component';
import { WorkingHoursComponent } from 'src/app/components/working-hours/working-hours.component';
import { CONVERSATION_STATUS } from 'src/app/constants';
import { fileNameFromImageModalUpload, genericCompareFn } from 'src/app/utils/utils';
import { CustomerViewSettings, SlackChannel, SubStatus, Template, TicketType } from 'src/models';
import { Project } from 'src/models/projects';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackService } from 'src/services/slack.service';
import { SubStatusesService } from 'src/services/sub-statuses.service';
import { TicketTypesService } from 'src/services/ticket-types.service';
import { UploadService } from 'src/services/upload.service';

@Component({
  selector: 'app-settings',
  templateUrl: './admin-settings.component.html',
  styleUrls: ['./admin-settings.component.scss'],
})
export class AdminSettingsComponent implements OnInit {
  closeStaleConversationsMode: string;
  closeConversationsAfterHours: number;
  sendRemindersForStaleIntakesAfterHours: number | null;
  aiSuggestResponsesEnabled: boolean;
  customerViewEnabled: boolean;
  customerViewSettings: CustomerViewSettings;
  teamAssignmentLoadBalancingMethod: string;
  moveToNeedsResponse: boolean;
  conversationTimeoutOptions: { value: number; label: string }[] = [
    { value: 1, label: '1 hour' },
    { value: 2, label: '2 hours' },
    { value: 4, label: '4 hours' },
    { value: 6, label: '6 hours' },
    { value: 8, label: '8 hours' },
    { value: 24, label: '1 day' },
    { value: 48, label: '2 days' },
    { value: 72, label: '3 days' },
    { value: 96, label: '4 days' },
    { value: 120, label: '5 days' },
    { value: 144, label: '6 days' },
    { value: 168, label: '7 days' },
    { value: 336, label: '14 days' },
    { value: 720, label: '30 days' },
    { value: 1440, label: '60 days' },
    { value: 2160, label: '90 days' },
  ];
  autoresponder: {
    enabled: boolean;
    condition: string;
    message: string;
    includeKnowledgeBaseLinks: string;
    includeAiResponse: string;
    alternateMessageEnabled: boolean;
    alternateMessage: string;
  };
  postCloseTemplateEnabled: boolean;
  postCloseTemplateId: string | null;
  postCloseTemplate?: Template;
  defaultTriageChannelId: any;
  triageChannel?: any;
  email: string;
  supportName: string;
  supportLogo: string;
  workingHoursEnabled: boolean;
  isSaveConfirmed = false;
  postCloseTicketTypes: Partial<TicketType>[];
  postCloseSubStatuses: Partial<SubStatus>[];
  availableClosedSubStatuses: Partial<SubStatus>[] = [];
  postCloseMaxOnce = false;
  isShowingAdvanced = false;
  disableConversationsStartedByInternalUser: boolean;
  autoSplittingEnabled: boolean;
  doNotReopenConversationsOnReply: boolean;
  isSlackDisconnected = false;
  defaultProject?: Partial<Project>;
  requireUnthreadSend = false;
  users: {
    phoneNumberRequired?: boolean;
    customProfileFields?: any[];
  };

  constructor(
    private authService: AuthService,
    private msgService: MessageService,
    private slackService: SlackService,
    private uploadService: UploadService,
    private msgSrvc: MessageService,
    private searchablePopoverService: SearchablePopoverService,
    private ticketTypesService: TicketTypesService,
    private subStatusesService: SubStatusesService,
    private apiService: ApiService,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
  ) {}
  async ngOnInit() {
    // Show initial values so it's not a blank screen
    this.showValuesForTenant();

    this.load();
  }

  showValuesForTenant() {
    const tenant = this.authService.tenant;
    this.defaultTriageChannelId = _.get(tenant, 'defaultTriageChannelId', null);
    this.teamAssignmentLoadBalancingMethod = tenant?.teamAssignmentLoadBalancingMethod ?? 'random';
    this.closeConversationsAfterHours = tenant?.closeConversationsAfterHours ?? null;
    this.closeStaleConversationsMode = tenant?.closeStaleConversationsMode ?? 'always';
    this.sendRemindersForStaleIntakesAfterHours = tenant?.sendRemindersForStaleIntakesAfterHours ?? null;
    this.aiSuggestResponsesEnabled = tenant?.aiFeatures?.suggestResponses ?? false;
    this.customerViewEnabled = tenant?.customerViewEnabled ?? false;
    this.customerViewSettings = tenant?.customerViewSettings ?? {};
    this.moveToNeedsResponse = tenant?.moveToNeedsResponse ?? false;
    this.email = tenant?.email;
    this.supportName = tenant?.supportName;
    this.supportLogo = tenant?.supportLogo;
    this.workingHoursEnabled = _.get(tenant, 'workingHours.enabled', false);
    this.postCloseTemplateEnabled = tenant?.postCloseSettings?.enabled ?? false;
    this.postCloseTemplateId = tenant.postCloseSettings?.slackTemplateId ?? null;
    this.postCloseMaxOnce = tenant.postCloseSettings?.sendMaxOnce ?? false;
    this.disableConversationsStartedByInternalUser = tenant.disableConversationsStartedByInternalUser;
    this.autoSplittingEnabled = tenant.autoSplittingEnabled;
    this.doNotReopenConversationsOnReply = tenant.doNotReopenConversationsOnReply;
    this.requireUnthreadSend = Boolean(!tenant.defaultToPublicMessages);
    this.defaultProject = this.authService.projects.find((project) => project.id === tenant.defaultProjectId);
    this.users = {
      phoneNumberRequired: tenant?.users?.phoneNumberRequired ?? false,
      customProfileFields: tenant?.users?.customProfileFields ?? [],
    };

    this.autoresponder = _.get(tenant, 'autoresponder', {
      enabled: false,
      condition: 'always',
      message: '',
    });
  }

  async load() {
    await this.authService.loadCompany();

    const tenant = this.authService.tenant;
    this.showValuesForTenant();

    if (this.defaultTriageChannelId) {
      await this.loadTriageChannel();
    }

    if (this.postCloseTemplateId) {
      await this.loadPostCloseTemplate();
    }

    if (tenant?.postCloseSettings?.ticketTypeIds?.length) {
      this.loadTicketTypes(tenant?.postCloseSettings?.ticketTypeIds);
    }

    if (tenant?.postCloseSettings?.subStatusKeys?.length) {
      this.loadSubStatuses(tenant?.postCloseSettings?.subStatusKeys);
    }

    this.loadAvailableClosedSubStatuses();
  }

  async loadTriageChannel() {
    try {
      const triageChannel = await this.slackService.getChannel(this.authService.tenant.defaultTriageChannelId);

      if (triageChannel) {
        this.triageChannel = triageChannel;
      }
    } catch (e) {
      if (e.status === 500) {
        this.isSlackDisconnected = true;
      } else {
        this.msgService.showError(e);
      }
    }
  }

  async loadPostCloseTemplate() {
    const result = (await this.apiService.getPromise(`/templates/${this.postCloseTemplateId}`)) as Template;

    this.postCloseTemplate = result;
  }

  async loadTicketTypes(ids: string[]) {
    const ticketTypesResponse = await this.ticketTypesService.list({
      limit: null,
      order: ['name'],
      descending: false,
      where: [
        {
          field: 'id',
          operator: 'in',
          value: ids,
        },
      ],
    });

    this.postCloseTicketTypes = ticketTypesResponse.data;
  }

  async loadSubStatuses(keys: string[]) {
    const subStatusKeys: Partial<SubStatus>[] = await this.apiService.getPromise('/sub-statuses/by-key');

    this.postCloseSubStatuses = subStatusKeys.filter((subStatus) => keys.includes(subStatus.key));
  }

  async loadAvailableClosedSubStatuses() {
    const closedSubStatusesResponse = await this.subStatusesService.list({
      limit: null,
      order: ['name'],
      descending: false,
      where: [
        {
          field: 'type',
          operator: '==',
          value: 'closed',
        },
      ],
    });

    this.availableClosedSubStatuses = closedSubStatusesResponse.data;
  }

  async confirmSave() {
    const confirm = await this.alertCtrl.create({
      header: 'Enable customer facing view of support requests?',
      message:
        'You are about to enable the customer facing view of support requests for all existing customers. This will pin a message to every channel allowing customers to view their open and closed issues. Do you want to proceed?',
      buttons: [
        {
          text: 'Nevermind',
          handler: () => {},
        },
        {
          text: 'Confirm',
          handler: async () => {
            this.isSaveConfirmed = true;
            this.save();
          },
        },
      ],
    });

    confirm.present();
  }

  async save() {
    const tenantUpdates: any = {
      teamAssignmentLoadBalancingMethod: this.teamAssignmentLoadBalancingMethod,
      closeConversationsAfterHours: this.closeConversationsAfterHours,
      closeStaleConversationsMode: this.closeStaleConversationsMode,
      sendRemindersForStaleIntakesAfterHours: this.sendRemindersForStaleIntakesAfterHours,
      customerViewEnabled: this.customerViewEnabled,
      customerViewSettings: this.customerViewSettings,
      autoresponder: this.autoresponder,
      moveToNeedsResponse: this.moveToNeedsResponse,
      postCloseSettings: {
        enabled: this.postCloseTemplateEnabled,
        slackTemplateId: this.postCloseTemplateId || null,
        ticketTypeIds: this.postCloseTicketTypes?.length ? this.postCloseTicketTypes.map((ticket) => ticket.id) : null,
        subStatusKeys: this.postCloseSubStatuses?.length
          ? this.postCloseSubStatuses.map((subStatus) => subStatus.key)
          : null,
        sendMaxOnce: this.postCloseMaxOnce,
      },
      defaultProjectId: this.defaultProject?.id ?? null,
      disableConversationsStartedByInternalUser: this.disableConversationsStartedByInternalUser,
      autoSplittingEnabled: this.autoSplittingEnabled,
      doNotReopenConversationsOnReply: this.doNotReopenConversationsOnReply,
      defaultToPublicMessages: !this.requireUnthreadSend,
      users: this.users,
    };

    if (this.email) {
      tenantUpdates.email = this.email;
    }

    if (this.supportName) {
      tenantUpdates.supportName = this.supportName;
    }

    if (this.supportLogo) {
      tenantUpdates.supportLogo = this.supportLogo;
    }

    if (this.defaultTriageChannelId !== undefined) {
      tenantUpdates.defaultTriageChannelId = this.defaultTriageChannelId;
    }

    const requireSaveConfirmation =
      this.authService.tenant.customerViewEnabled !== this.customerViewEnabled && this.customerViewEnabled;

    if (requireSaveConfirmation && !this.isSaveConfirmed) {
      this.confirmSave();
    } else {
      try {
        await this.authService.updateTenant(tenantUpdates);

        this.load();
        this.msgService.showBottom('✔︎ Saved');
        this.isSaveConfirmed = false;
      } catch (err) {
        this.msgService.showBottom(`Something went wrong: ${err.message}`);
      }
    }
  }

  async uploadSupportLogo() {
    const modal = await this.modalCtrl.create({
      component: ImageModalComponent,
      componentProps: {
        searchValue: '',
        helperText: '',
        aspectRatio: 1,
      },
    });
    await modal.present();

    const { data } = await modal.onWillDismiss();

    // if backdrop is clicked and no data passed
    if (!data || !data.uploadFile) {
      return;
    }

    this.uploadService
      .upload([data.uploadFile], `slack`, fileNameFromImageModalUpload(data.uploadFile))
      .then(() => {
        this.supportLogo = data.uploadFile.url;

        this.save();
      })
      .catch((err) => {
        this.msgSrvc.show(err.message);
      });
  }

  async manageWorkingHours() {
    const modal = await this.modalCtrl.create({
      component: WorkingHoursComponent,
      componentProps: {
        originalWorkingHours: this.authService.tenant?.workingHours ?? {},
        onSave: async (updates: any) => this.authService.updateTenant(updates),
      },
    });

    modal.present();
  }

  onTriageChannelChange(channel?: SlackChannel) {
    this.defaultTriageChannelId = channel?.id ?? null;
    this.triageChannel = channel;

    this.save();
  }

  closeConversationsAfterHoursCompareFn(v1, v2) {
    return genericCompareFn(v1, v2);
  }

  sendRemindersForStaleIntakesAfterHoursCompareFn(v1, v2) {
    return genericCompareFn(v1, v2);
  }

  autoresponderDidChange(autoresponder: any) {
    this.autoresponder = autoresponder;
  }

  customUserFieldsDidChange() {
    this.users.customProfileFields = [...this.users.customProfileFields];
  }

  choosePostCloseTemplate() {
    this.searchablePopoverService.templates({
      event,
      side: 'top',
      templateType: 'slack',
      callback: (id: string, _title: string, template: Template) => {
        this.postCloseTemplateId = id;
        this.postCloseTemplate = template;
      },
    });
  }

  removeTicketType(index: number) {
    this.postCloseTicketTypes.splice(index, 1);
  }

  removePostCloseSubStatus(index: number) {
    this.postCloseSubStatuses.splice(index, 1);
  }

  addTicketType() {
    this.searchablePopoverService.ticketTypes({
      event,
      showNoneOption: false,
      callback: (_id: string, _title: string, data: TicketType) => {
        if (!this.postCloseTicketTypes) {
          this.postCloseTicketTypes = [data];
        } else {
          this.postCloseTicketTypes = _.uniqBy([...this.postCloseTicketTypes, data], (t) => t.id);
        }
      },
    });
  }

  addPostCloseSubStatus() {
    this.searchablePopoverService.subStatusKeys({
      event,
      showNoneOption: false,
      type: CONVERSATION_STATUS.CLOSED,
      callback: (_id: string, _title: string, data: SubStatus) => {
        if (!this.postCloseSubStatuses) {
          this.postCloseSubStatuses = [data];
        } else {
          this.postCloseSubStatuses = _.uniqBy([...this.postCloseSubStatuses, data], (s) => s.key);
        }
      },
    });
  }

  setDefaultProject() {
    this.searchablePopoverService.projects({
      event,
      showNoneOption: false,
      callback: (_id: string, _title: string, data: Project) => {
        this.defaultProject = data;
      },
    });
  }

  getProjectSettingsLink() {
    return `/dashboard/settings/projects/${this.defaultProject?.id}/settings`;
  }

  onMessageContentChange(event) {
    this.customerViewSettings.customMessage = event;
  }
}
